import React, { useMemo, useEffect, useState, useCallback } from "react";
import type { ColDef, IDatasource, IGetRowsParams } from "ag-grid-community";
import type { RowSelectionOptions } from "ag-grid-community";
import type { GridApi } from "ag-grid-community";
import axios from "axios";
import useAppData from "@/store/useAppData";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faClock, faWarehouse, faListCheck, faShirt } from "@fortawesome/free-solid-svg-icons";
import type { FloatingAction } from "../FloatingActions";
import type { Item, FilterKind, Stock } from "./types";
import type { ItemListCardProps } from "./itemListOP";

function mapFilter(filter?: FilterKind): ColDef["filter"] {
  if (filter === "text") return "agTextColumnFilter";
  if (filter === "number") return "agNumberColumnFilter";
  if (filter === "set") return "agSetColumnFilter";
  return false;
}

const useItemListLogicOP = ({
  columns,
  fetchItems,
  scope,
  onPrintQRCodes
}: ItemListCardProps) => {

  const setSelectedStock = useAppData(state => state.setSelectedStock);
  const selectedStock = useAppData(state => state.selectedStock);
  const selectedModule = useAppData(state => state.selectedModule);
  const loggedUser = useAppData(state => state.user);
  const gridApiRef = React.useRef<GridApi<Item> | null>(null);
  const [showDialogArchiveItem, setShowDialogArchiveItem] = useState<boolean>(false);

  const router = useRouter(
    useShallow(state => ({
      go: state.go,
      history: state.history
    }))
  );

  const rowSelection: RowSelectionOptions<Item> = {
    mode: "multiRow",
    checkboxes: true,
    headerCheckbox: true,
  };

  const actions: FloatingAction[] = [
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "1",
      label: "Příjem a výdej zaměstnanci",
      icon: <FontAwesomeIcon icon={faUser} />,
      onClick: () => router.go({ key: RouterStoreItemKey.RELEASECARD })
    }] : []),
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "2",
      label: "Příjem na sklad",
      icon: <FontAwesomeIcon icon={faWarehouse} />,
      onClick: () => router.go({ key: RouterStoreItemKey.STOCKINGCARD })
    }] : []),
    ...(loggedUser?.userrole != "viewer" ? [{
      key: "3",
      label: "Inventura",
      icon: <FontAwesomeIcon icon={faListCheck} />,
      onClick: () => router.go({ key: RouterStoreItemKey.INVENTORY })
    }] : []),
    ...(selectedStock?.usecleaners && loggedUser?.userrole != "viewer" ? [{
      key: "3",
      label: "Příjem z čistírny",
      icon: <FontAwesomeIcon icon={faShirt} />,
      onClick: () => router.go({ key: RouterStoreItemKey.NOTRETURNEDCARD })
    }] : []),
    {
      key: "4",
      label: "Skladové pohyby",
      icon: <FontAwesomeIcon icon={faClock} />,
      onClick: () => router.go({ key: RouterStoreItemKey.HISTORYITEMS })
    }
  ];

  const [selectedUsersState, setSelectedUsersState] = React.useState<any[]>([]);
  const [selectedUserState, setSelectedUserState] = React.useState<string | null>(null);
  const [selectedStockState, setSelectedStockState] = React.useState<string>();
  const [stocksState, setStocksState] = React.useState<Stock[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [countsByState, setCountsByState] = useState<Array<{ key: string; count: number }>>([]);
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const [dialogText, setDialogText] = useState<string>("");
  const [refresh, setRefresh] = useState<boolean>(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    loadStocks();
  }, [selectedModule]);

  const loadStocks = async () => {

    try {
      const res = await (
        axios({
          method: "post",
          url: "api/stock/getAll",
          data: {
            userId: loggedUser?.id,
            module: selectedModule?.id
          }
        })
      );

      if (!res?.data?.stocks?.length || res?.data?.stocks?.length === 0) {
        setSelectedStockState(undefined);
        setSelectedStock(null);

        setStocksState([]);

        return;
      }

      setStocksState(res.data.stocks);

      if (selectedStock && res.data.stocks.find(stock => stock.id === selectedStock.id)) {
        setSelectedStockState(selectedStock?.id);
      } else {
        setSelectedStockState(String(res.data.stocks[0].id))
        setSelectedStock(res.data.stocks[0]);
      }
    } catch (err) {
      throw err;
    }
  }

  const loadUsers = async () => {
    try {
      const res = await (
        axios({
          method: "post",
          url: "api/user/getAll",
        })
      );

      setSelectedUsersState(res.data.users);

    } catch (err) {
      throw err;
    }
  };

  const archiveStockItem = async (stockItemId: string) => {
    try {
      const res = await axios({
        method: "post",
        url: "/api/stockitem/archive",
        data: {
          id: stockItemId
        },
      });

      return res.data.id;
    } catch (err) {
      console.error("Failed to update issued item", err);
      throw err;
    }
  };

  const getIssuedItemsByStockItems = async (stockItemIds: string[]) => {
    try {
      const res = await (
        axios({
          method: "post",
          url: "api/issuedItem/getByStockItems",
          data: {
            stockItemIds: stockItemIds
          }
        })
      );

      return res?.data?.issuedItems ?? [];
    } catch (err) {
      throw err;
    }
  };

  const columnDefs = useMemo<ColDef<Item>[]>(() => {

    return columns.map((c) => ({
      field: c.field as any,
      headerName: c.headerName,
      flex: c.flex,
      filter: mapFilter(c.filter),
      floatingFilter: c.floatingFilter,
      valueFormatter: c?.valueFormatter,
      checkboxSelection: c?.checkboxSelection,
      headerCheckboxSelection: c?.headerCheckboxSelection,
      maxWidth: c?.maxWidth,
      editable: c?.editable
    }));
  }, [columns]);

  const datasource = useMemo<IDatasource>(() => ({
    getRows: async (params: IGetRowsParams) => {
      console.log("selectedUser:" + selectedUserState);
      console.log("selectedStock130:" + selectedStockState);
      try {

        if (!selectedStockState || !selectedStock) {
          console.log("selectedStock120:" + selectedStock);
          setTotalCount(0);
          setCountsByState([]);

          params.successCallback([], 0);

          return;
        }

        const result = await fetchItems({
          startRow: params.startRow,
          endRow: params.endRow,
          agParams: { stockId: selectedStockState, selectedUser: selectedUserState, ...params }
        });

        setTotalCount(result.totalQuantity ?? 0);
        setCountsByState(result.countsByState ?? []);

        params.successCallback(result.rows, result.totalCount);
      } catch {
        params.failCallback();
      }
    },
  }), [fetchItems, scope, selectedStock, selectedStockState, selectedUserState, refresh]);

  const setSelectedUserId = (userId: string) => {
    setSelectedUserState(userId);
  };

  const printBarcodes = () => {
    const rows = gridApiRef.current?.getSelectedRows() ?? [];

    if (onPrintQRCodes) {
      onPrintQRCodes(rows);
    }
  };

  const archiveSelectedStockItems = async () => {
    const rows = gridApiRef.current?.getSelectedRows() ?? [];

    if (rows.length > 0) {

      const stockItemQuantity = rows.filter(row => row.quantity > 0);
      if (stockItemQuantity.length > 0) {
        setDialogText(`Některé položky nelze archivovat (Id): (${stockItemQuantity.map(item => item.id).join(",")})`);
        setShowDialog(true);
        return;
      }

      const stockItemIds = rows.map(row => row.id);
      const stockItemsByIssuedItems = await getIssuedItemsByStockItems(stockItemIds);

      if (stockItemsByIssuedItems && stockItemsByIssuedItems.length > 0) {
        const stockItemIdsWithoutDuplicates = [
          ...new Set(stockItemsByIssuedItems.map((item: any) => item.stockitemid))
        ];

        setDialogText(`Některé položky nelze archivovat (Id): (${stockItemIdsWithoutDuplicates.join(",")})`);
        setShowDialog(true);
        return;
      }

      for (const row of rows) {
        await archiveStockItem(row.id);
      }

      gridApiRef.current.deselectAll();
      gridApiRef.current?.refreshInfiniteCache();
    }
  };

  const onClickArchiveSelectedStockItems = async () => {
    const rows = gridApiRef.current?.getSelectedRows() ?? [];

    if (rows.length > 0) {
      setShowDialogArchiveItem(true);
    }
  };

  const onChangeSelectedStock = (e) => {
    setSelectedStock(stocksState.filter(stock => stock.id == e.target.value)[0]);
    setSelectedStockState(e.target.value);

    gridApiRef?.current?.deselectAll();
    gridApiRef?.current?.refreshInfiniteCache();
  }

  const updateQauntity = async (id: string, quantity: string) => {
    try {

      const res = await axios({
        method: "post",
        url: "/api/stockitem/updateQuantity",
        data: {
          id: id,
          quantity: quantity
        },
      });

      return res.data.id;
    } catch (err) {
      console.error("Failed to update stock item", err);
      throw err;
    }
  };

  const handleCellValueChanged = async (params: any) => {
    const { colDef, data, oldValue, newValue, value } = {
      colDef: params.colDef,
      data: params.data,
      oldValue: params.oldValue,
      newValue: params.newValue,
      value: params.value,
    };

    if (colDef.field === 'quantity') {

      if (newValue && newValue > -1) {
        await updateQauntity(data.id, newValue);
      }

      gridApiRef.current?.refreshInfiniteCache();
    }
  };

  return {
    setSelectedStock,
    selectedStock,
    loggedUser,
    gridApiRef,
    showDialogArchiveItem,
    setShowDialogArchiveItem,
    rowSelection,
    actions,
    selectedUsersState,
    selectedUserState,
    selectedStockState,
    setSelectedStockState,
    stocksState,
    totalCount,
    countsByState,
    showDialog,
    setShowDialog,
    dialogText,
    refresh,
    setRefresh,

    loadStocks,
    loadUsers,
    archiveStockItem,
    getIssuedItemsByStockItems,

    columnDefs,
    datasource,

    setSelectedUserId,
    printBarcodes,

    archiveSelectedStockItems,
    onClickArchiveSelectedStockItems,

    handleCellValueChanged,
    onChangeSelectedStock
  };
};

export default useItemListLogicOP;